import { getRouteApi } from "@tanstack/react-router";
import { Users, X } from "lucide-react";
import { useState } from "react";
import { useDebounce } from "react-use";
import { type z } from "zod";
import { SearchFilter } from "./SearchFilter";
import { cleanFilters } from "./utils";
import { type UserStatus } from "./index";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils/common";
import { type userFiltersSchema } from "@/routes/(authed)/_layout.settings.users";
import { formatUserCount } from "@/settings/utils";

export const USER_ROLES = ["Admin", "Guest", "Manager", "Worker"] as const;

export type UserFilters = z.infer<typeof userFiltersSchema>;

interface UserTableFiltersProps {
  filters: UserFilters;
  userCount: number;
  filteredCount: number;
  loading?: boolean;
  error?: Error | null;
}

const statusOptions = [
  { value: "all", label: "All statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "INVITED", label: "Invited" },
] as const;

const lastLoginOptions = [
  { value: "all", label: "All users" },
  { value: "never", label: "Never logged in" },
  { value: "recent", label: "Last 30 days" },
  { value: "older", label: "Older than 30 days" },
] as const;

export type LastLoginOption = (typeof lastLoginOptions)[number]["value"];

export function UserTableFilters({ filters, userCount, filteredCount, loading = false, error }: UserTableFiltersProps) {
  const isDisabled = loading || !!error;

  // Non-search filters use local state for immediate UI feedback
  const [displayFilters, setDisplayFilters] = useState<UserFilters>({
    status: filters.status,
    role: filters.role,
    showOwnersOnly: filters.showOwnersOnly,
    lastLoginFilter: filters.lastLoginFilter,
  });

  const navigate = getRouteApi("/(authed)/_layout/settings/users").useNavigate();

  useDebounce(
    () => {
      const updatedFilters: UserFilters = {
        ...filters, // Keep search from URL
        ...displayFilters, // Apply other filter changes
      };
      const cleanedFilters = cleanFilters(updatedFilters);
      void navigate({ search: cleanedFilters });
    },
    500,
    [displayFilters],
  );

  const updateFilter = <K extends keyof UserFilters>(key: K, value: UserFilters[K]) => {
    setDisplayFilters({ ...displayFilters, [key]: value });
  };

  const clearAllFilters = () => {
    setDisplayFilters({});
    // Also clear search by navigating immediately
    void navigate({ search: {} });
  };

  // Check active filters including committed search from URL
  const activeFilters = hasActiveFilters({ ...displayFilters, search: filters.search });

  return (
    <div className="space-y-3">
      {/* Search and Filter Controls */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <SearchFilter disabled={isDisabled} />

        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filter */}
          <Select
            value={displayFilters.status || "all"}
            onValueChange={(value) => {
              if (value === "all") {
                updateFilter("status", undefined);
              } else {
                updateFilter("status", value as UserStatus);
              }
            }}
            disabled={isDisabled}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Role Filter */}
          <Select
            value={displayFilters.role || "all"}
            onValueChange={(value) => {
              if (value === "all") {
                updateFilter("role", undefined);
              } else {
                updateFilter("role", value as (typeof USER_ROLES)[number]);
              }
            }}
            disabled={isDisabled}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {USER_ROLES.map((role) => (
                <SelectItem key={role} value={role}>
                  {role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Last Login Filter */}
          <Select
            value={displayFilters.lastLoginFilter || "all"}
            onValueChange={(value: LastLoginOption) =>
              updateFilter("lastLoginFilter", value === "all" ? undefined : value)
            }
            disabled={isDisabled}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {lastLoginOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Owner Filter */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="owners-only"
              checked={displayFilters.showOwnersOnly || false}
              onCheckedChange={(checked) => updateFilter("showOwnersOnly", checked ? true : undefined)}
              disabled={isDisabled}
            />
            <label
              htmlFor="owners-only"
              className={cn(
                "text-sm font-normal whitespace-nowrap",
                isDisabled ? "text-muted-foreground cursor-not-allowed" : "cursor-pointer",
              )}
            >
              Owners only
            </label>
          </div>
        </div>
      </div>

      {/* Active Filters and Results - Always render container to prevent layout shift */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 min-h-[32px]">
        <div className="flex items-center gap-2 flex-wrap">
          {activeFilters ? (
            <>
              <span className="text-sm text-muted-foreground">Filters:</span>
              {filters.search ? (
                <Badge variant="secondary">
                  Search: {filters.search}
                  <button
                    onClick={() => {
                      // Clear search by navigating with empty search
                      const updatedFilters = { ...filters, search: undefined };
                      const cleanedFilters = cleanFilters(updatedFilters);
                      void navigate({ search: cleanedFilters });
                    }}
                    className="ml-1 hover:bg-secondary-foreground/20 rounded"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ) : null}
              {displayFilters.status ? (
                <Badge variant="secondary">
                  {statusOptions.find((opt) => opt.value === displayFilters.status)?.label}
                  <button
                    onClick={() => updateFilter("status", undefined)}
                    className="ml-1 hover:bg-secondary-foreground/20 rounded"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ) : null}
              {displayFilters.role ? (
                <Badge variant="secondary">
                  Role: {displayFilters.role}
                  <button
                    onClick={() => updateFilter("role", undefined)}
                    className="ml-1 hover:bg-secondary-foreground/20 rounded"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ) : null}
              {displayFilters.showOwnersOnly ? (
                <Badge variant="secondary">
                  Owners only
                  <button
                    onClick={() => updateFilter("showOwnersOnly", undefined)}
                    className="ml-1 hover:bg-secondary-foreground/20 rounded"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ) : null}
              {displayFilters.lastLoginFilter && displayFilters.lastLoginFilter !== "all" ? (
                <Badge variant="secondary">
                  {lastLoginOptions.find((opt) => opt.value === displayFilters.lastLoginFilter)?.label}
                  <button
                    onClick={() => updateFilter("lastLoginFilter", undefined)}
                    className="ml-1 hover:bg-secondary-foreground/20 rounded"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ) : null}
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-auto p-1 text-xs">
                Clear all
              </Button>
            </>
          ) : null}
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4" />
          <span>{formatUserCount(filteredCount, userCount)}</span>
        </div>
      </div>
    </div>
  );
}

export function hasActiveFilters(filters: UserFilters): boolean {
  return Boolean(
    filters.search ||
      filters.status ||
      filters.role ||
      filters.showOwnersOnly ||
      (filters.lastLoginFilter && filters.lastLoginFilter !== "all"),
  );
}
